import React, { useMemo, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { cloneDeep, omit, orderBy } from "lodash";

import {
  TemplateFragment,
  UserSummaryFragment,
} from "../../../graphql/fragments";
import useGetTemplates from "../../../graphql/hooks/queries/useGetTemplates";
import useTranslations from "../../../hooks/useTranslations";

import { FormStepProps } from "../../../screens/MadFormScreen";
import { Button, Container, H3, TailSpin, ScreenHeader } from "../../wombat-ui";
import { MadIcon } from "../../icons";

import { TemplatePreviewCard } from "./TemplatePreviewModal";

import TemplateForm from "../../templates/TemplateForm";

import { DEFAULT_TEMPLATE } from "../../../screens/TemplateFormScreen";
import { StringParam, useQueryParam } from "use-query-params";

type NextStepButtonControlProps = {
  formState: ReturnType<typeof useForm>["formState"];
  onNext: () => void;
};

const NextStepButtonControl: React.FC<NextStepButtonControlProps> = React.memo(
  ({ formState, onNext }) => {
    const t = useTranslations();
    const disabled = useMemo(() => {
      return formState.errors.templateAttributes !== undefined;
    }, [formState]);

    return (
      <Button disabled={disabled} onClick={onNext} id="selectTemplateButton">
        {t("forms.actions.next")}
      </Button>
    );
  }
);

const InnerStep: React.FC<
  FormStepProps & {
    template: TemplateFragment;
    onTemplateChange: () => void;
  }
> = React.memo(
  ({
    register,
    unregister,
    setValue,
    getValues,
    template,
    control,
    formState,
    errors,
    onTemplateChange,
    onNext,
    reset,
  }) => {
    const t = useTranslations();
    const [valuesSet, setValuesSet] = useState(false);

    useEffect(() => {
      const { templateSections, ...templateAttributes } =
        template || DEFAULT_TEMPLATE;

      let sections = templateSections.map(({ templateQuestions, ...ts }) => ({
        ...ts,
        templateQuestionsAttributes: templateQuestions.map((tq) => ({
          ...tq,
          referentIds: [],
        })),
      }));
      sections = orderBy(
        sections.map((e) => omit(e, "id", "__typename")),
        "order",
        "asc"
      );
      sections.map((s) => {
        s.templateQuestionsAttributes = orderBy(
          s.templateQuestionsAttributes.map((tq) =>
            omit(
              tq,
              "__typename",
              "referents",
              "id",
              "optionsSchema",
              "defaultValue",
              "defaultValueSchema",
              "referents"
            )
          ),
          "order",
          "asc"
        );
      });

      setValue(
        "templateAttributes",
        cloneDeep({
          ...templateAttributes,
          templateSectionsAttributes: sections,
        }),
        { shouldValidate: true }
      );

      // Okay this bit is rather ugly but I couldn't find any other way to
      // set the values and trigger a propre rerender, a simple set on the
      // same tick wouldn't cut it
      setTimeout(() => setValuesSet(true));
    }, [template]);

    if (!valuesSet) {
      return null;
    }

    return (
      <>
        <ScreenHeader
          icon={MadIcon}
          title={t("mads.new")}
          description={template?.title || t("mads.customFollowUp")}
          actions={[
            <Button
              variant="subtle"
              onClick={onTemplateChange}
              key="back"
              id="changeTemplateButton"
            >
              {t("mads.changeTemplate")}
            </Button>,
            <NextStepButtonControl
              onNext={onNext}
              formState={formState}
              key="next"
            />,
          ]}
        />
        <Container className={`flex-grow w-full`}>
          <div className="flex flex-col space-y-4">
            <TemplateForm
              shouldSelectReferent
              defaultValue={template}
              name="templateAttributes"
              errors={errors?.templateAttributes}
              {...{
                register,
                unregister,
                setValue,
                getValues,
                control,
                formState,
                reset,
              }}
            />
          </div>
        </Container>
      </>
    );
  }
);

const SecondStep: React.FC<
  FormStepProps
> = ({
  onNext,
  register,
  unregister,
  setValue,
  getValues,
  formState,
  control,
  errors,
  reset,
}) => {
  const t = useTranslations();

  const { data, loading } = useGetTemplates({ variables: { madId: null } });
  const templates = data?.templates?.collection || [];

  const [innerStep, setInnerStep] = useState(false);
  const [
    selectedTemplate,
    setSelectedTemplate,
  ] = useState<TemplateFragment | null>(null);

  /**
   * Utility callback to switch templates
   */
  const [QPTemplateId, setQPTemplateId] = useQueryParam(
    "template",
    StringParam
  );

  useEffect(() => {
    // If we have a template already selected
    if (QPTemplateId) {
      const selected = templates?.find(
        (template) => template.slug === QPTemplateId
      );
      // If the id resolve to a template
      if (selected) {
        setSelectedTemplate(selected);
        setInnerStep(true);
      }
    } else if (QPTemplateId === null) {
      setSelectedTemplate(null);
      setInnerStep(true);
    } else setInnerStep(false);
  }, [QPTemplateId, templates]);

  const defaultTemplates = useMemo(
    () => templates?.filter((template) => template.default),
    [templates]
  );
  const userTemplates = useMemo(
    () => templates?.filter((template) => !template.default),
    [templates]
  );

  if (!innerStep)
    return (
      <>
        <ScreenHeader
          icon={MadIcon}
          title={t("mads.new")}
          description={t("mads.content")}
        />
        <Container className={`flex-col flex-grow w-full`}>
          <div className="flex flex-col space-y-6">
            <div className="flex justify-between">
              <H3>{t("mads.mooveoTemplates")}</H3>
              <Button onClick={() => setQPTemplateId(null)}>
                {t("mads.customFollowUp")}
              </Button>
            </div>
            {!loading && (
              <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {defaultTemplates.map((template) => (
                  <TemplatePreviewCard
                    template={template}
                    key={template.id}
                    onSelect={setQPTemplateId}
                  />
                ))}
              </ul>
            )}
            {loading && (
              <div className="flex flex-col items-center justify-center flex-grow">
                <TailSpin className="w-8 h-8" />
              </div>
            )}
            <div>
              <H3>{t("mads.yourTemplates")}</H3>
            </div>
            {!loading && (
              <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {userTemplates.map((template) => (
                  <TemplatePreviewCard
                    template={template}
                    key={template.id}
                    onSelect={setQPTemplateId}
                  />
                ))}
              </ul>
            )}
            {loading && (
              <div className="flex flex-col items-center justify-center flex-grow">
                <TailSpin className="w-8 h-8" />
              </div>
            )}
          </div>
        </Container>
      </>
    );

  return (
    <InnerStep
      template={selectedTemplate}
      onTemplateChange={() => setQPTemplateId(undefined)}
      {...{
        register,
        unregister,
        setValue,
        getValues,
        control,
        onNext,
        errors,
        formState,
        reset,
      }}
    />
  );
};

export default React.memo(SecondStep);
